import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { getCourseContent, markLessonComplete, getCourseProgress } from '../../api/educentialApi';
import { ChevronLeft, ChevronRight, CheckCircle, Circle, ArrowLeft, ArrowRight } from 'lucide-react';
import './CourseLearning.css';

function CourseLearning() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [progress, setProgress] = useState({});
  const [courseProgress, setCourseProgress] = useState(null);

  const [currentLesson, setCurrentLesson] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [completingLesson, setCompletingLesson] = useState(false);

  useEffect(() => {
    loadCourseContent();
    loadProgress();
  }, [id]);

  const loadCourseContent = async () => {
    try {
      setLoading(true);
      const response = await getCourseContent(id);
      if (response.success) {
        setCourse(response.data.course);
        setEnrollment(response.data.enrollment);

        // Build progress map
        const progressMap = {};
        response.data.progress.forEach((p) => {
          progressMap[p.lessonId] = p;
        });
        setProgress(progressMap);

        // Set first lesson as current
        if (response.data.course.Modules?.length > 0) {
          const firstModule = response.data.course.Modules[0];
          if (firstModule.Lessons?.length > 0) {
            setCurrentLesson(firstModule.Lessons[0]);
          }
        }
      }
    } catch (err) {
      if (err.message.includes('Not enrolled')) {
        alert('You need to enroll in this course first');
        navigate(`/courses/${id}`);
      } else {
        alert('Failed to load course: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await getCourseProgress(id);
      if (response.success) {
        setCourseProgress(response.data);
      }
    } catch (err) {
      console.error('Failed to load progress:', err);
    }
  };

  const handleMarkComplete = async () => {
    if (!currentLesson) return;

    try {
      setCompletingLesson(true);
      const response = await markLessonComplete(currentLesson.id);
      if (response.success) {
        // Update progress map
        setProgress({
          ...progress,
          [currentLesson.id]: response.data.lessonProgress,
        });
        // Update course progress
        setCourseProgress(response.data.courseProgress);

        // Move to next lesson
        const nextLesson = getNextLesson();
        if (nextLesson) {
          setCurrentLesson(nextLesson);
        }
      }
    } catch (err) {
      alert('Failed to mark lesson complete: ' + err.message);
    } finally {
      setCompletingLesson(false);
    }
  };

  const getNextLesson = () => {
    if (!course || !currentLesson) return null;

    for (let i = 0; i < course.Modules.length; i++) {
      const module = course.Modules[i];
      const lessonIndex = module.Lessons.findIndex((l) => l.id === currentLesson.id);

      if (lessonIndex !== -1) {
        // Found current lesson
        if (lessonIndex < module.Lessons.length - 1) {
          // Next lesson in same module
          return module.Lessons[lessonIndex + 1];
        } else if (i < course.Modules.length - 1) {
          // First lesson of next module
          return course.Modules[i + 1].Lessons[0];
        }
      }
    }

    return null;
  };

  const getPreviousLesson = () => {
    if (!course || !currentLesson) return null;

    for (let i = 0; i < course.Modules.length; i++) {
      const module = course.Modules[i];
      const lessonIndex = module.Lessons.findIndex((l) => l.id === currentLesson.id);

      if (lessonIndex !== -1) {
        if (lessonIndex > 0) {
          // Previous lesson in same module
          return module.Lessons[lessonIndex - 1];
        } else if (i > 0) {
          // Last lesson of previous module
          const prevModule = course.Modules[i - 1];
          return prevModule.Lessons[prevModule.Lessons.length - 1];
        }
      }
    }

    return null;
  };

  const isLessonCompleted = (lessonId) => {
    return progress[lessonId]?.completed || false;
  };

  if (loading) {
    return (
      <div className="learning-container">
        <div className="loading">Loading course...</div>
      </div>
    );
  }

  if (!course || !currentLesson) {
    return (
      <div className="learning-container">
        <div className="error">Course not found or no lessons available</div>
      </div>
    );
  }

  const nextLesson = getNextLesson();
  const prevLesson = getPreviousLesson();
  const isCompleted = isLessonCompleted(currentLesson.id);

  return (
    <div className="learning-container">
      {/* Sidebar */}
      <div className={`learning-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>{course.title}</h2>
          <button className="btn-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {courseProgress && (
          <div className="course-progress-bar">
            <div className="progress-info">
              <span>Progress</span>
              <span className="progress-percentage">{courseProgress.progress}%</span>
            </div>
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{ width: `${courseProgress.progress}%` }}
              />
            </div>
            <div className="progress-stats">
              {courseProgress.completedLessons} of {courseProgress.totalLessons} lessons
            </div>
          </div>
        )}

        <div className="modules-list">
          {course.Modules.map((module) => (
            <div key={module.id} className="sidebar-module">
              <h3 className="module-title">{module.title}</h3>
              <div className="lessons-list">
                {module.Lessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    className={`lesson-item ${currentLesson.id === lesson.id ? 'active' : ''} ${
                      isLessonCompleted(lesson.id) ? 'completed' : ''
                    }`}
                    onClick={() => setCurrentLesson(lesson)}
                  >
                    <div className="lesson-check">
                      {isLessonCompleted(lesson.id) ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="lesson-info">
                      <div className="lesson-title">{lesson.title}</div>
                      <div className="lesson-meta">
                        <span className={`lesson-type ${lesson.type.toLowerCase()}`}>
                          {lesson.type}
                        </span>
                        {lesson.duration && <span>{lesson.duration} min</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="learning-main">
        <div className="lesson-header">
          <div>
            <h1>{currentLesson.title}</h1>
            <div className="lesson-meta-header">
              <span className={`lesson-type ${currentLesson.type.toLowerCase()}`}>
                {currentLesson.type}
              </span>
              {currentLesson.duration && <span>{currentLesson.duration} minutes</span>}
            </div>
          </div>
          {isCompleted && (
            <div className="completed-badge flex items-center space-x-1">
              <CheckCircle className="w-4 h-4" />
              <span>Completed</span>
            </div>
          )}
        </div>

        <div className="lesson-content">
          {currentLesson.type === 'VIDEO' && currentLesson.videoUrl && (
            <div className="video-player">
              <ReactPlayer
                url={currentLesson.videoUrl}
                controls
                width="100%"
                height="100%"
                config={{
                  youtube: {
                    playerVars: { showinfo: 1 },
                  },
                }}
              />
            </div>
          )}

          {currentLesson.type === 'ARTICLE' && currentLesson.content && (
            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: currentLesson.content }}
            />
          )}

          {currentLesson.type === 'DOCUMENT' && currentLesson.fileUrl && (
            <div className="document-viewer">
              <iframe
                src={currentLesson.fileUrl}
                title={currentLesson.title}
                width="100%"
                height="800px"
                style={{ border: 'none', borderRadius: '8px' }}
              />
              <div className="pdf-controls">
                <a
                  href={currentLesson.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-download"
                >
                  Open in New Tab
                </a>
              </div>
            </div>
          )}

          {(currentLesson.type === 'QUIZ' || currentLesson.type === 'ASSIGNMENT') && (
            <div className="placeholder-content">
              <p>This {currentLesson.type.toLowerCase()} feature is coming soon!</p>
            </div>
          )}
        </div>

        <div className="lesson-actions">
          <div className="navigation-buttons">
            <button
              className="btn-nav flex items-center space-x-2"
              onClick={() => prevLesson && setCurrentLesson(prevLesson)}
              disabled={!prevLesson}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Previous Lesson</span>
            </button>

            {!isCompleted && (
              <button
                className="btn-complete"
                onClick={handleMarkComplete}
                disabled={completingLesson}
              >
                {completingLesson ? 'Completing...' : 'Mark as Complete'}
              </button>
            )}

            <button
              className="btn-nav flex items-center space-x-2"
              onClick={() => nextLesson && setCurrentLesson(nextLesson)}
              disabled={!nextLesson}
            >
              <span>Next Lesson</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseLearning;
